import { USER_LOCAL_STORAGE_KEY } from "@/configs/site";

export const AGENT_SESSION_KEYS = {
  estate: "tlx-agent-session:v1:estate",
  board: "tlx-agent-session:v1:board",
  protection: "tlx-agent-session:v1:protection",
} as const;

type StoreName = keyof typeof AGENT_SESSION_KEYS;
type ResetName = StoreName | "tenant-enforcement" | "oauth-client-profiles";
const resetters = new Map<ResetName, () => void>();

/** No store imports here: authentication must not load the mock-data graph. */
export const registerAgentSessionReset = (
  name: ResetName,
  reset: () => void,
) => {
  resetters.set(name, reset);
};

export function clearAgentSession(): void {
  for (const key of Object.values(AGENT_SESSION_KEYS)) removeSnapshot(key);
  for (const reset of resetters.values()) reset();
}

/** The signed-in user record from storage, or undefined if unreadable. */
function storedUser(): Record<string, unknown> | undefined {
  try {
    const user: unknown = JSON.parse(
      localStorage.getItem(USER_LOCAL_STORAGE_KEY) || "{}",
    );
    return isRecord(user) ? user : undefined;
  } catch {
    return undefined;
  }
}

/** The signed-in user's tenant, or "" when none is stored. */
export const currentTenant = (): string => String(storedUser()?.tenant ?? "");

/** Tokens rotate during a login session, so they are deliberately not its key. */
export function agentSessionScope(demoProfile: boolean): string {
  const user = storedUser();
  return JSON.stringify([
    user?.tenant ?? null,
    user?.userId ?? null,
    demoProfile,
  ]);
}

function removeSnapshot(key: string): void {
  try {
    sessionStorage.removeItem(key);
  } catch {
    // Storage can be blocked; the in-memory session still works.
  }
}

export function readAgentSession<T>(
  name: StoreName,
  scope: string,
  validate: (value: unknown) => value is T,
): T | undefined {
  const key = AGENT_SESSION_KEYS[name];
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return undefined;
    const saved: unknown = JSON.parse(raw);
    if (
      isRecord(saved) &&
      saved.version === 1 &&
      saved.scope === scope &&
      validate(saved.state)
    )
      return saved.state;
  } catch {
    // Invalid or inaccessible storage must never prevent the page from loading.
  }
  removeSnapshot(key);
  return undefined;
}

export function writeAgentSession(
  name: StoreName,
  scope: string,
  state: unknown,
): void {
  const key = AGENT_SESSION_KEYS[name];
  try {
    sessionStorage.setItem(key, JSON.stringify({ version: 1, scope, state }));
  } catch {
    // Do not restore an older snapshot after a write failed (for example quota).
    removeSnapshot(key);
  }
}

export const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

export const isString = (value: unknown): value is string =>
  typeof value === "string";

export const hasStrings = (value: Record<string, unknown>, keys: string[]) =>
  keys.every(key => isString(value[key]));

export const hasOptionalStrings = (
  value: Record<string, unknown>,
  keys: string[],
) => keys.every(key => value[key] === undefined || isString(value[key]));

export const isList = (
  value: unknown,
  validate: (item: unknown) => boolean,
): value is unknown[] => Array.isArray(value) && value.every(validate);

export const isEntries = (
  value: unknown,
  validate: (item: unknown) => boolean,
) =>
  isList(
    value,
    item =>
      Array.isArray(item) &&
      item.length === 2 &&
      isString(item[0]) &&
      validate(item[1]),
  );

export const isTimestamp = (value: unknown): value is string =>
  isString(value) && Number.isFinite(Date.parse(value));

export const isCount = (value: unknown): value is number =>
  typeof value === "number" && Number.isInteger(value) && value >= 0;
